/**
 * One status pipe, client half (Master Plan V2, Phase 13 Build 51a).
 *
 * `fetchOpsSources()` in opsStatus.ts fans out to THIRTEEN GETs every 30s —
 * on every page, in every open tab. `GET /api/status-rollup` answers all
 * thirteen in one response, from the same backend handlers, so the browser
 * spends one request per poll instead of thirteen and its six per-origin
 * HTTP/1.1 connections stay free for the data the operator is waiting on.
 *
 * Shape-preserving by construction: the rollup carries the same
 * `{data, error}` envelope per source that `settle()` produces, so the
 * derivation below it (deriveSubsystems / rollup / the dot / the bell) is
 * untouched and no rendered value can move.
 *
 * Deploy-order safety: the fan-out stays as the fallback. A backend without
 * this endpoint answers 404, we stop asking for the rest of the session, and
 * the store behaves exactly as it did before — so backend and frontend can
 * ship in either order.
 */
import type {
  AlertRow, AlertSummaryOut, CircuitState, EngineState, FreshnessAlert,
  FreshnessOverviewResponse, FreshnessUnifiedOut, HealthOut, OpsDeepHealth,
  ReadinessOut, Run, SchedulerJob, SyncJobsOut,
} from './api'
import type { OpsSources, Src } from './opsStatus'

/** One source's envelope on the wire — mirrors Src<T>. */
export type RollupSrc = { data: unknown; error: string | null }

export type StatusRollupBody = {
  generated_at?: string
  sources?: Record<string, RollupSrc>
}

/**
 * Wire key (backend, snake_case) → OpsSources key (client, camelCase).
 * Every key is required: a rollup missing one is treated as unusable and we
 * fall back, rather than silently handing the store an `undefined` source
 * that would render as a blank subsystem instead of a real severity.
 */
export const ROLLUP_KEYS: Record<string, keyof OpsSources> = {
  health: 'health',
  readiness: 'readiness',
  engine: 'engine',
  jobs: 'jobs',
  runs: 'runs',
  circuit: 'circuit',
  freshness: 'freshness',
  freshness_alerts: 'freshnessAlerts',
  alerts: 'alerts',
  alert_summary: 'alertSummary',
  ops_deep: 'opsDeep',
  sync_jobs: 'syncJobs',
  unified: 'unified',
}

function src<T>(e: RollupSrc | undefined): Src<T> {
  return { data: (e?.data ?? null) as T | null, error: e?.error ?? null }
}

/**
 * Map a rollup body onto OpsSources, or null if it isn't usable.
 *
 * Returning null (rather than a half-filled object) is what makes the
 * fallback safe: a partially-understood rollup would blank subsystems, and a
 * blank subsystem reads as "unknown" on a dot an operator trusts.
 */
export function mapRollup(body: StatusRollupBody | null | undefined): OpsSources | null {
  const s = body?.sources
  if (!s || typeof s !== 'object') return null
  for (const wireKey of Object.keys(ROLLUP_KEYS)) {
    if (!(wireKey in s)) return null
  }
  return {
    health: src<HealthOut>(s.health),
    readiness: src<ReadinessOut>(s.readiness),
    engine: src<EngineState>(s.engine),
    jobs: src<SchedulerJob[]>(s.jobs),
    runs: src<Run[]>(s.runs),
    circuit: src<CircuitState>(s.circuit),
    freshness: src<FreshnessOverviewResponse>(s.freshness),
    freshnessAlerts: src<FreshnessAlert[]>(s.freshness_alerts),
    alerts: src<AlertRow[]>(s.alerts),
    alertSummary: src<AlertSummaryOut>(s.alert_summary),
    opsDeep: src<OpsDeepHealth>(s.ops_deep),
    syncJobs: src<SyncJobsOut>(s.sync_jobs),
    unified: src<FreshnessUnifiedOut>(s.unified),
  }
}

// ── Availability policy ─────────────────────────────────────────────────────
//
// The point of the rollup is to REMOVE requests. Probing a backend that will
// never answer, once every 30s forever, would add one instead — so a 404 (or
// a body we can't map) disables it for the session, and repeated failures of
// any other kind do the same after a few tries. A reload re-probes.

const MAX_ROLLUP_FAILS = 3

let disabled = false
let consecutiveFails = 0

export function statusRollupDisabled(): boolean {
  return disabled
}

/** Test hook — module-level state would otherwise leak between cases. */
export function __resetStatusRollupState(): void {
  disabled = false
  consecutiveFails = 0
}

/**
 * One request for all thirteen sources, or null if the rollup can't serve us
 * (caller then runs the fan-out). Never throws.
 *
 * `fresh` appends `?fresh=1`, which tells the backend to bypass its short
 * single-flight cache and rebuild from the live sources (backend/api/
 * status_rollup.py). Used by opsStatus.ts's refetchFresh() so a post-fix
 * verify can never be answered from a pre-fix cached body. The disable/
 * retry policy below is unchanged either way — a fresh read that 404s or
 * fails still counts toward the same session-wide fallback decision.
 */
export async function tryStatusRollup(fresh = false): Promise<OpsSources | null> {
  if (disabled) return null
  try {
    const res = await fetch(fresh ? '/api/status-rollup?fresh=1' : '/api/status-rollup')
    if (res.status === 404) {
      // Backend predates this endpoint (or it was removed) — stop asking.
      disabled = true
      return null
    }
    if (!res.ok) throw new Error(`status-rollup ${res.status}`)
    const mapped = mapRollup(await res.json() as StatusRollupBody)
    if (!mapped) {
      disabled = true
      return null
    }
    consecutiveFails = 0
    return mapped
  } catch {
    consecutiveFails += 1
    if (consecutiveFails >= MAX_ROLLUP_FAILS) disabled = true
    return null
  }
}
